import { useState } from 'react';
import { Crown, Sparkles, X, Lock, Mic, Brain, FileText, Palette, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PricingModal } from '@/components/UpgradeBanner';

// Feature info for upgrade prompts
const FEATURE_INFO = {
  voice_typing: {
    icon: Mic,
    name: 'Voice Typing',
    description: 'Speak your thoughts and let AI transcribe them instantly.',
    limitMessage: (limit) => `You've used all ${limit} voice transcriptions this month.`,
  },
  ai_coach: {
    icon: Brain,
    name: 'AI Coach',
    description: 'Get personalized reflections and insights on your journal entries.',
    limitMessage: (limit) => `You've used all ${limit} AI reflections this month.`,
  },
  journal_entries: {
    icon: FileText,
    name: 'Journal Entries',
    description: 'Unlimited daily journaling to capture your thoughts.',
    limitMessage: (limit) => `You've reached your ${limit} entries limit this month.`,
  },
  export_pdf: {
    icon: FileText,
    name: 'PDF Export',
    description: 'Download and print your journal entries as beautiful PDFs.',
    limitMessage: () => 'PDF export is a Premium feature.',
  },
  custom_themes: {
    icon: Palette,
    name: 'Custom Themes',
    description: 'Personalize your journal with custom colors and themes.',
    limitMessage: () => 'Custom themes are a Premium feature.',
  },
  mood_insights: {
    icon: TrendingUp,
    name: 'Mood Insights',
    description: 'Track patterns and trends in your emotional wellbeing.',
    limitMessage: () => 'Full mood insights are a Premium feature.',
  },
};

/**
 * UpgradePrompt - Shows when a user tries to use a feature they've exceeded or don't have access to
 * 
 * Usage:
 * <UpgradePrompt 
 *   feature="voice_typing" 
 *   limit={10}
 *   isOpen={showUpgrade} 
 *   onClose={() => setShowUpgrade(false)} 
 * />
 */
export const UpgradePrompt = ({ feature, limit, isOpen, onClose }) => {
  const [showPricing, setShowPricing] = useState(false);
  
  const featureInfo = FEATURE_INFO[feature] || {
    icon: Lock,
    name: 'Premium Feature',
    description: 'Upgrade to Premium for unlimited access.',
    limitMessage: () => 'This feature requires Premium.',
  };
  
  const FeatureIcon = featureInfo.icon;
  
  if (!isOpen) return null;
  
  if (showPricing) {
    return <PricingModal isOpen={true} onClose={() => { setShowPricing(false); onClose(); }} />;
  }
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div 
        className="relative w-full max-w-md rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        style={{ background: 'var(--theme-background)' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-white/10 transition-colors z-10"
          style={{ color: 'var(--theme-text-muted)' }}
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Header with gradient */}
        <div 
          className="pt-8 pb-6 px-6 text-center"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(236, 72, 153, 0.15) 50%, rgba(249, 115, 22, 0.15) 100%)',
          }}
        >
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 50%, #F97316 100%)',
              boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)',
            }}
          >
            <FeatureIcon className="w-8 h-8 text-white" />
          </div>
          
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--theme-text)' }}>
            {featureInfo.name} Limit Reached
          </h2>
          
          <p className="text-sm" style={{ color: 'var(--theme-text-muted)' }}>
            {featureInfo.limitMessage(limit)}
          </p>
        </div>
        
        {/* Body */}
        <div className="p-6">
          <p className="text-center mb-6" style={{ color: 'var(--theme-text-secondary)' }}>
            Upgrade to <span className="font-semibold text-purple-400">Premium</span> for unlimited access to {featureInfo.name.toLowerCase()} and all other features.
          </p>
          
          {/* Benefits preview */}
          <div className="space-y-2 mb-6">
            {['Unlimited journal entries', 'Unlimited voice typing', 'Unlimited AI Coach', 'PDF exports & more'].map((benefit) => (
              <div key={benefit} className="flex items-center gap-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                <Sparkles className="w-4 h-4 text-purple-400 flex-shrink-0" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          
          {/* CTA Buttons */}
          <div className="space-y-3">
            <Button
              onClick={() => setShowPricing(true)}
              className="w-full py-3 font-semibold"
              style={{
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                color: 'white',
              }}
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Button>
            
            <button
              onClick={onClose}
              className="w-full py-2 text-sm font-medium transition-colors"
              style={{ color: 'var(--theme-text-muted)' }}
            >
              Maybe later
            </button>
          </div>
          
          <p className="text-center text-xs mt-4" style={{ color: 'var(--theme-text-muted)' }}>
            Starting at $6.25/month billed yearly
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * useUpgradePrompt - Hook to manage upgrade prompt state
 */
export const useUpgradePrompt = () => {
  const [promptState, setPromptState] = useState({
    isOpen: false,
    feature: null,
    limit: null,
  });
  
  const showUpgradePrompt = (feature, limit = null) => {
    setPromptState({ isOpen: true, feature, limit });
  };
  
  const hideUpgradePrompt = () => {
    setPromptState({ isOpen: false, feature: null, limit: null });
  };
  
  return {
    ...promptState,
    showUpgradePrompt,
    hideUpgradePrompt,
  };
};

/**
 * FeatureLock - Wrapper component that shows a lock overlay for disabled features
 */
export const FeatureLock = ({ feature, children, onClick }) => {
  const featureInfo = FEATURE_INFO[feature] || { icon: Lock, name: 'Premium Feature' };
  const FeatureIcon = featureInfo.icon;
  
  return (
    <div className="relative group cursor-pointer" onClick={onClick}>
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg backdrop-blur-[2px]">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/90 text-white text-sm font-medium">
          <Crown className="w-4 h-4" />
          Premium
        </div>
      </div>
    </div>
  );
};

export default UpgradePrompt;
